
import { computed, defineComponent, onBeforeMount, reactive, toRaw } from 'vue'

// Components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { DataState } from '@/core/classes/DataState.class'
import { DeliveryTaskT } from '@/core/types/delivery.types'

// composable
import useNotifications from '@/common/composable/useNotifications'

// realisations
import { delivery } from '@/core/realisations/delivery.realisation'

// types
import { ExpressTaskT } from '@/core/types/express.types'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-textarea': TheTextarea,
        'the-button': TheButton,
        Loader,
        Checkbox,
    },
    props: {
        stepNumber: {
            type: Number,
            default: 1,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        receiverIdx: {
            type: Number,
            default: null,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_saved: false,
            is_loading: false,
        })

        const payload = reactive({
            verification_invoice_number: null as string | null,
            extra_info: null as string | null,
            tasks: [] as ExpressTaskT[] | null,
            //
            is_spark_verification: false,
            is_kaspi_verification: false,
        })

        const data_state = reactive({
            tasks: new DataState<DeliveryTaskT>({}),
        })

        const rules = {
            extra_info: { required },
            verification_invoice_number: {
                required: requiredIf(() => payload.is_kaspi_verification),
            },
        }

        const state_form$ = useVuelidate(rules, payload)

        const emitReset = () => {
            state.is_saved = false

            payload.extra_info = null
            payload.verification_invoice_number = null
            payload.tasks = []

            state_form$.value.$reset()
            emit('reset', props.receiverIdx)
        }

        const emitSubmit = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            state.is_saved = true

            emit('submit', toRaw({ ...payload, index: props.receiverIdx }))
        }

        const loadTasks = async (page: number) => {
            try {
                data_state.tasks.is_loading = true

                const response = await delivery.getTasks(page)
                data_state.tasks.list = response.data

                data_state.tasks.is_failed = false
            } catch (error) {
                data_state.tasks.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При загрузке списка задач произошла ошибка',
                })
            } finally {
                data_state.tasks.is_loading = false
            }
        }

        const isChecked = (task_id: number) => {
            if (payload.tasks && payload.tasks.length)
                return payload.tasks.map((task) => task.id).includes(task_id)
        }

        const removeTask = (task_id: number) => {
            if (!payload.tasks || !payload.tasks.length) return

            const index = payload.tasks.findIndex((task) => task.id === task_id)
            if (index > -1) payload.tasks.splice(index, 1)
        }

        const selectTask = (task: DeliveryTaskT) => {
            if (payload.tasks) payload.tasks.push({ id: task.id, comment: task.title })
        }

        const onCheckboxChange = (type: string) => {
            if (type === 'spark') payload.is_kaspi_verification = false
            else payload.is_spark_verification = false
        }

        onBeforeMount(() => {
            loadTasks(1)
        })

        const getTasksString = computed(() => {
            let tasks_string = ''

            if (payload.tasks && payload.tasks.length) {
                payload.tasks.forEach((task) => {
                    let task_string = data_state.tasks.list.find(
                        (task) => task.id === task.id
                    )?.title

                    if (task_string)
                        tasks_string += tasks_string.length ? `, ${task_string}` : task_string
                })
            }

            return tasks_string
        })

        return {
            state,
            payload,
            data_state,
            //
            state_form$,
            //
            emitReset,
            emitSubmit,
            loadTasks,
            isChecked,
            removeTask,
            selectTask,
            onCheckboxChange,
            //
            getTasksString,
        }
    },
})
